import React from 'react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { formatPrice } from '../data/components';
import { X, Sparkles, Users, Briefcase, Tent, PartyPopper, Bed, UtensilsCrossed, Shield, Mountain, Megaphone, ArrowRight } from 'lucide-react';

const CONFIGURATIONS = [
  {
    id: 'luxury-4',
    name: 'Luxury 4-Seater',
    icon: Sparkles,
    desc: 'Premium travel for 4 with full entertainment and comfort',
    budget: 'Premium',
    price: 485000,
    color: '#8b5cf6',
    components: [
      { comp_id: 'seat-recliner-motorized', x: 2, y: 3, rotation: 0 },
      { comp_id: 'seat-recliner-motorized', x: 7, y: 3, rotation: 0 },
      { comp_id: 'seat-captain-premium', x: 2, y: 13, rotation: 0 },
      { comp_id: 'seat-captain-premium', x: 7, y: 13, rotation: 0 },
      { comp_id: 'elec-tv-retract', x: 4, y: 7, rotation: 0 },
      { comp_id: 'elec-sound-premium', x: 5, y: 3, rotation: 0 },
      { comp_id: 'util-fridge-drawer', x: 4, y: 16, rotation: 0 },
      { comp_id: 'elec-control', x: 5, y: 18, rotation: 0 },
      { comp_id: 'elec-charger', x: 4, y: 13, rotation: 0 },
    ]
  },
  {
    id: 'family-camper',
    name: 'Family Camper',
    icon: Tent,
    desc: 'Camping-ready with kitchen, sleeping and outdoor gear',
    budget: 'Mid',
    price: 395000,
    color: '#10b981',
    components: [
      { comp_id: 'seat-captain-standard', x: 2, y: 3, rotation: 0 },
      { comp_id: 'seat-captain-standard', x: 7, y: 3, rotation: 0 },
      { comp_id: 'seat-sofa-bed', x: 3, y: 6, rotation: 0 },
      { comp_id: 'util-pantry-compact', x: 3, y: 13, rotation: 0 },
      { comp_id: 'util-fridge-single', x: 6, y: 13, rotation: 0 },
      { comp_id: 'life-tent-rear', x: 4, y: 17, rotation: 0 },
      { comp_id: 'store-overhead', x: 4, y: 3, rotation: 0 },
    ]
  },
  {
    id: 'executive-office',
    name: 'Executive Office',
    icon: Briefcase,
    desc: 'Mobile office with workspace, connectivity and privacy',
    budget: 'Premium',
    price: 420000,
    color: '#3b82f6',
    components: [
      { comp_id: 'seat-captain-premium', x: 2, y: 3, rotation: 0 },
      { comp_id: 'seat-captain-premium', x: 7, y: 3, rotation: 0 },
      { comp_id: 'util-tray-table', x: 4, y: 5, rotation: 0 },
      { comp_id: 'seat-captain-premium', x: 2, y: 13, rotation: 0 },
      { comp_id: 'seat-captain-premium', x: 7, y: 13, rotation: 0 },
      { comp_id: 'elec-tv-fixed', x: 4, y: 16, rotation: 0 },
      { comp_id: 'store-partition', x: 4, y: 7, rotation: 0 },
      { comp_id: 'elec-control', x: 5, y: 18, rotation: 0 },
      { comp_id: 'elec-charger', x: 4, y: 13, rotation: 0 },
    ]
  },
  {
    id: 'party-van',
    name: 'Party Van',
    icon: PartyPopper,
    desc: 'Entertainment on wheels with surround sound and lounge',
    budget: 'Premium',
    price: 510000,
    color: '#f43f5e',
    components: [
      { comp_id: 'seat-sofa-bed', x: 2, y: 3, rotation: 0 },
      { comp_id: 'seat-captain-premium', x: 8, y: 3, rotation: 0 },
      { comp_id: 'elec-sound-premium', x: 4, y: 6, rotation: 0 },
      { comp_id: 'elec-tv-retract', x: 5, y: 13, rotation: 0 },
      { comp_id: 'util-fridge-single', x: 4, y: 16, rotation: 0 },
      { comp_id: 'seat-recliner-manual', x: 2, y: 13, rotation: 0 },
      { comp_id: 'seat-recliner-manual', x: 8, y: 13, rotation: 0 },
      { comp_id: 'elec-control', x: 5, y: 18, rotation: 0 },
      { comp_id: 'elec-charger', x: 4, y: 3, rotation: 0 },
    ]
  },
  {
    id: 'sleeper',
    name: 'Long Distance Sleeper',
    icon: Bed,
    desc: 'Designed for overnight journeys with beds and washroom',
    budget: 'Premium',
    price: 580000,
    color: '#6366f1',
    components: [
      { comp_id: 'seat-sofa-bed', x: 2, y: 3, rotation: 0 },
      { comp_id: 'seat-sofa-bed', x: 2, y: 13, rotation: 0 },
      { comp_id: 'util-toilet', x: 7, y: 3, rotation: 0 },
      { comp_id: 'store-wardrobe', x: 7, y: 13, rotation: 0 },
      { comp_id: 'elec-tv-fixed', x: 4, y: 16, rotation: 0 },
      { comp_id: 'elec-charger', x: 5, y: 18, rotation: 0 },
    ]
  },
  {
    id: 'mobile-kitchen',
    name: 'Mobile Kitchen',
    icon: UtensilsCrossed,
    desc: 'Full catering setup for events and outdoor cooking',
    budget: 'Mid',
    price: 365000,
    color: '#f59e0b',
    components: [
      { comp_id: 'util-pantry-full', x: 3, y: 3, rotation: 0 },
      { comp_id: 'util-fridge-single', x: 6, y: 3, rotation: 0 },
      { comp_id: 'util-bbq-grill', x: 3, y: 13, rotation: 0 },
      { comp_id: 'store-overhead', x: 6, y: 6, rotation: 0 },
      { comp_id: 'store-overhead', x: 6, y: 13, rotation: 0 },
      { comp_id: 'util-tray-table', x: 6, y: 15, rotation: 0 },
      { comp_id: 'seat-foldable', x: 4, y: 16, rotation: 0 },
      { comp_id: 'seat-foldable', x: 6, y: 16, rotation: 0 },
    ]
  },
  {
    id: 'vip-transport',
    name: 'VIP Transport',
    icon: Shield,
    desc: 'High-security dignitary transport with premium amenities',
    budget: 'Premium',
    price: 620000,
    color: '#0ea5e9',
    components: [
      { comp_id: 'seat-recliner-motorized', x: 2, y: 3, rotation: 0 },
      { comp_id: 'seat-recliner-motorized', x: 7, y: 3, rotation: 0 },
      { comp_id: 'store-partition', x: 4, y: 3, rotation: 0 },
      { comp_id: 'elec-tv-retract', x: 4, y: 13, rotation: 0 },
      { comp_id: 'util-fridge-drawer', x: 4, y: 15, rotation: 0 },
      { comp_id: 'elec-sound-premium', x: 4, y: 17, rotation: 0 },
      { comp_id: 'elec-control', x: 5, y: 6, rotation: 0 },
      { comp_id: 'elec-charger', x: 5, y: 13, rotation: 0 },
    ]
  },
  {
    id: 'adventure',
    name: 'Adventure Camping',
    icon: Mountain,
    desc: 'Rugged outdoor-ready with tent, canopy and cooking',
    budget: 'Budget',
    price: 245000,
    color: '#84cc16',
    components: [
      { comp_id: 'seat-captain-standard', x: 2, y: 3, rotation: 0 },
      { comp_id: 'seat-captain-standard', x: 7, y: 3, rotation: 0 },
      { comp_id: 'seat-foldable', x: 4, y: 6, rotation: 0 },
      { comp_id: 'seat-foldable', x: 6, y: 6, rotation: 0 },
      { comp_id: 'util-pantry-compact', x: 4, y: 13, rotation: 0 },
      { comp_id: 'life-canopy', x: 4, y: 15, rotation: 0 },
      { comp_id: 'life-tent-rear', x: 4, y: 17, rotation: 0 },
      { comp_id: 'store-underseat', x: 4, y: 3, rotation: 0 },
    ]
  },
  {
    id: 'election-campaign',
    name: 'Election Campaign',
    icon: Megaphone,
    desc: 'Campaign vehicle with hydraulic lift, PA system and seating',
    budget: 'Premium',
    price: 750000,
    color: '#ef4444',
    components: [
      { comp_id: 'elec-platform-lift', x: 3, y: 3, rotation: 0 },
      { comp_id: 'elec-sound-premium', x: 4, y: 13, rotation: 0 },
      { comp_id: 'elec-sound-premium', x: 4, y: 15, rotation: 0 },
      { comp_id: 'seat-captain-standard', x: 8, y: 3, rotation: 0 },
      { comp_id: 'seat-captain-standard', x: 8, y: 6, rotation: 0 },
      { comp_id: 'elec-tv-fixed', x: 8, y: 13, rotation: 0 },
      { comp_id: 'util-fridge-single', x: 8, y: 16, rotation: 0 },
      { comp_id: 'elec-control', x: 5, y: 17, rotation: 0 },
      { comp_id: 'elec-charger', x: 4, y: 17, rotation: 0 },
    ]
  },
  {
    id: 'budget-basic',
    name: 'Budget Basic',
    icon: Users,
    desc: 'Functional 8-seater with essential amenities under 2L',
    budget: 'Budget',
    price: 196000,
    color: '#64748b',
    components: [
      { comp_id: 'seat-captain-standard', x: 2, y: 3, rotation: 0 },
      { comp_id: 'seat-captain-standard', x: 7, y: 3, rotation: 0 },
      { comp_id: 'seat-captain-standard', x: 2, y: 6, rotation: 0 },
      { comp_id: 'seat-captain-standard', x: 7, y: 6, rotation: 0 },
      { comp_id: 'seat-foldable', x: 3, y: 13, rotation: 0 },
      { comp_id: 'seat-foldable', x: 5, y: 13, rotation: 0 },
      { comp_id: 'seat-foldable', x: 7, y: 13, rotation: 0 },
      { comp_id: 'elec-sound-basic', x: 5, y: 16, rotation: 0 },
    ]
  },
];

export default function ConfigurationPicker({ onSelectConfig, onStartFresh, onClose }) {
  return (
    <div className="fixed inset-0 z-[90] bg-black/70 flex items-center justify-center p-4" data-testid="config-picker-overlay">
      <div className="bg-slate-900 rounded-2xl w-full max-w-5xl max-h-[85vh] overflow-hidden border border-white/10 shadow-2xl" data-testid="config-picker">
        {/* Header */}
        <div className="px-8 py-6 border-b border-white/5 flex items-center justify-between">
          <div>
            <h2 className="font-chivo font-bold text-2xl text-white mb-1">Choose Your Starting Point</h2>
            <p className="text-sm text-slate-400">Pick a pre-built configuration or start from scratch</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-slate-400 hover:text-white" data-testid="config-close-btn">
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Start Fresh */}
        <div className="px-8 py-4 border-b border-white/5">
          <Button
            data-testid="start-fresh-btn"
            onClick={onStartFresh}
            variant="outline"
            className="w-full border-dashed border-slate-700 text-slate-400 hover:text-white hover:border-blue-500/50 py-6 gap-2"
          >
            <ArrowRight className="w-4 h-4" />
            Start with Empty Canvas
          </Button>
        </div>

        {/* Configurations Grid */}
        <ScrollArea className="h-[calc(85vh-180px)]">
          <div className="px-8 py-6 grid md:grid-cols-2 gap-4">
            {CONFIGURATIONS.map(config => (
              <div
                key={config.id}
                data-testid={`config-${config.id}`}
                onClick={() => onSelectConfig(config)}
                className="group glass-card rounded-xl p-5 cursor-pointer hover:border-blue-500/30 transition-all duration-300 hover:-translate-y-0.5"
              >
                <div className="flex items-start gap-4">
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                    style={{ background: `${config.color}20`, border: `1px solid ${config.color}40` }}
                  >
                    <config.icon className="w-5 h-5" style={{ color: config.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-chivo font-semibold text-white group-hover:text-blue-400 transition-colors">{config.name}</h3>
                      <Badge className="text-[9px] h-4" style={{ background: `${config.color}20`, color: config.color, border: 'none' }}>
                        {config.budget}
                      </Badge>
                    </div>
                    <p className="text-xs text-slate-500 mb-2 leading-relaxed">{config.desc}</p>
                    <div className="flex items-center gap-3">
                      <span className="font-jetbrains text-xs text-blue-400">{formatPrice(config.price)}</span>
                      <span className="font-jetbrains text-[10px] text-slate-600">{config.components.length} components</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}

export { CONFIGURATIONS };
